<template>
  <div
    class="notification-toast-card cursor-pointer px-3.5 pb-3.5 pt-3 outline-none"
    :data-testid="item ? 'notification-toast' : 'notification-toast-burst'"
    role="button"
    tabindex="0"
    @click="onActivate?.()"
    @keydown.enter.prevent="onActivate?.()"
    @keydown.space.prevent="onActivate?.()"
  >
    <template v-if="item">
      <div class="flex items-center gap-2 pr-7">
        <!-- NOTIF-011: an export being prepared wears a spinner instead of the level chip -->
        <span
          v-if="view.state === 'preparing'"
          class="inline-flex items-center gap-1 text-[10px] text-text-muted"
          data-testid="notification-export-spinner"
        >
          <i class="ri-loader-4-line animate-spin text-xs" aria-hidden="true" />
          Preparing
        </span>
        <NotificationLevelBadge v-else :level="item.level" />
        <span class="text-[10px] text-text-muted tabular-nums whitespace-nowrap">
          {{ formatRelativeTime(view.at) }}
        </span>
      </div>
      <p class="notification-toast-title mt-2 text-[13px] font-semibold leading-snug text-text">
        {{ item.title }}
      </p>
      <p
        v-if="view.facts"
        class="notification-toast-body mt-1 line-clamp-2 text-[11px] leading-relaxed text-text-secondary"
      >
        {{ view.facts }}
      </p>
      <p class="mt-2 flex items-center gap-1.5 text-[10px] text-text-muted">
        <span class="truncate">{{ view.meta }}</span>
        <!-- One file: a button to look at (the card itself is the action and downloads); several: the dropdown (C2) -->
        <ExportDownloadMenu
          v-if="view.state === 'ready' && item && view.export"
          :item="item"
          :exp="view.export"
          size="xs"
          inline
          root-class="ml-auto"
        />
      </p>
    </template>

    <template v-else>
      <div class="flex items-start gap-2.5 pr-7">
        <span
          class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-hover"
          aria-hidden="true"
        >
          <i class="ri-notification-3-line text-sm text-text-secondary" />
        </span>
        <div class="min-w-0">
          <p class="notification-toast-title text-[13px] font-semibold leading-snug text-text">
            {{ count }}{{ truncated ? '+' : '' }} new notifications
          </p>
          <p class="mt-0.5 text-[11px] leading-relaxed text-text-secondary">
            Review them together in the notification panel.
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
/**
 * NOTIF-003 — the body of an in-app notification toast (Readdy design, 2026-10-21): a quiet
 * card with the level chip, title, body and page; or the burst roll-up "N new notifications".
 * No buttons: the whole card is the action (`onActivate` — the bell panel, or the deep link
 * for Action needed; the toast dismisses on the same click), the × is BaseToast's and only
 * closes. Nothing is read from the card itself: a Heads-up / Info is read by resting the
 * cursor on its row in the panel, an Action needed once its page is open.
 *
 * NOTIF-011 — an export's card: a spinner and "Started …" while preparing, the facts and a
 * Download hint once ready (the click downloads — the store's rule).
 */
import { computed } from 'vue'
import ExportDownloadMenu from '@/components/notifications/ExportDownloadMenu.vue'
import NotificationLevelBadge from '@/components/notifications/NotificationLevelBadge.vue'
import type { NotificationItem } from '@/services/notificationsApi'
import { rowView, type NotificationRowView } from '@/lib/notifications/exports'
import { formatRelativeTime } from '@/utils/datetimeUtils'

const props = defineProps<{
  /** The notification; absent for the burst variant. */
  item?: NotificationItem
  /** Burst variant: how many arrived (and whether the window was full). */
  count?: number
  truncated?: boolean
  /** A click / Enter on the card. */
  onActivate?: () => void
}>()

const view = computed<NotificationRowView>(() =>
  props.item
    ? rowView(props.item, formatRelativeTime)
    : { export: null, state: null, facts: '', meta: '', at: '' }
)
</script>
